import { extractErrorMessage, request } from '@/utils/ajax';

// ---- AWS (Elastic Beanstalk + CloudWatch) ----

/** Tab order on the page. */
export const stages = ['test', 'uat', 'prod'] as const;
export type Stage = (typeof stages)[number];

export type Health = 'ok' | 'degraded' | 'down' | 'unknown';

export interface AwsServiceHealth {
    /** EB application name, e.g. bygr-api-core. */
    name: string;
    environmentName: string;
    health: Health;
    /** EB enhanced health status: Ok, Warning, Degraded, Severe, … */
    healthStatus: string | null;
    /** EB environment status: Ready, Updating, Launching, … */
    status: string | null;
    causes: string[];
    instancesOk: number | null;
    instancesTotal: number | null;
    /** Last 15 minutes; null when the environment has no load balancer of its own (or we may not read its metrics). */
    requestsPerMinute: number | null;
    errorRate: number | null;
    p95Ms: number | null;
    updatedAt: Date | null;
    versionLabel: string | null;
}

/** Every stage's health from the same EB listing; unhealthy services carry no instance health or metrics. */
export interface AwsStageSummary {
    stage: Stage;
    health: Health;
    total: number;
    unhealthy: AwsServiceHealth[];
}

export type HealthEventSource = 'deploy' | 'eb';

export interface AwsHealthEvent {
    id: string;
    source: HealthEventSource;
    severity: 'info' | 'warning' | 'error';
    service: string;
    environmentName: string;
    message: string;
    at: Date;
}

export interface AwsAlarm {
    name: string;
    reason: string;
    since: Date | null;
}

/** One point per minute, oldest first, summed over every load balancer in the stage. */
export interface AwsMetricHistory {
    timestamps: Date[];
    requestsPerMinute: number[];
    errorRate: number[];
    p95Ms: (number | null)[];
    /** Null when no RDS instance is configured. */
    dbCpu: (number | null)[] | null;
}

export type AwsHealthSection = 'environments' | 'health' | 'metrics' | 'events' | 'alarms';

export interface AwsHealth {
    stage: Stage;
    generatedAt: Date;
    services: AwsServiceHealth[];
    stages: AwsStageSummary[];
    events: AwsHealthEvent[];
    /** Null when the alarms section failed (see errors). */
    alarms: AwsAlarm[] | null;
    /** Null when the metrics section failed (see errors). */
    metrics: AwsMetricHistory | null;
    /** Sections that failed on their own, usually a missing IAM permission. */
    errors: { section: AwsHealthSection; message: string }[];
}

type RawService = Omit<AwsServiceHealth, 'updatedAt'> & { updatedAt: string | null };

const toDate = (value: string | null) => (value ? new Date(value) : null);
const toService = (s: RawService): AwsServiceHealth => ({ ...s, updatedAt: toDate(s.updatedAt) });

/** Rejects with `request()`'s Response on failure; turn it into text with describeError. */
export async function getAwsHealth(stage: Stage): Promise<AwsHealth> {
    // no-store for the same reason as the Sentry call below: polled live data.
    const x = await request('api/system-health/aws', { query: { stage }, cache: 'no-store' }).then((r) => r.json());
    return {
        ...x,
        generatedAt: new Date(x.generatedAt),
        services: x.services.map(toService),
        stages: x.stages.map((s: Omit<AwsStageSummary, 'unhealthy'> & { unhealthy: RawService[] }) => ({
            ...s,
            unhealthy: s.unhealthy.map(toService),
        })),
        events: x.events.map((e: Omit<AwsHealthEvent, 'at'> & { at: string }) => ({ ...e, at: new Date(e.at) })),
        alarms:
            x.alarms?.map((a: Omit<AwsAlarm, 'since'> & { since: string | null }) => ({ ...a, since: toDate(a.since) })) ??
            null,
        metrics: x.metrics && { ...x.metrics, timestamps: x.metrics.timestamps.map((t: string) => new Date(t)) },
    };
}

export interface AwsInstance {
    id: string;
    health: Health;
    /** EB's enhanced status: Ok, Info, Warning, Degraded, Severe, Pending, NoData, Unknown. */
    healthStatus: string | null;
    /** EB's own explanation, e.g. "CPU utilization is greater than 95.0%". */
    causes: string[];
    instanceType: string | null;
    availabilityZone: string | null;
    launchedAt: Date | null;
    /** Deployed, Deploying, Pending or Failed. */
    deploymentStatus: string | null;
    deployedAt: Date | null;
    versionLabel: string | null;
    cpuPercent: number | null;
    loadAverage: number | null;
    /** EB's last ~10 second sample, scaled to a minute. */
    requestsPerMinute: number | null;
    status4xxPerMinute: number | null;
    status5xxPerMinute: number | null;
    p50Ms: number | null;
    p90Ms: number | null;
    p99Ms: number | null;
}

/** Everything behind a click on one service. */
export interface AwsEnvironmentReport {
    service: AwsServiceHealth;
    instances: AwsInstance[];
    /** The last day, newest first. */
    events: AwsHealthEvent[];
    /** Last 30 minutes; other ranges come from getAwsReportMetrics. */
    metrics: AwsReportMetrics | null;
    links: { dashboard: string; health: string; logs: string; monitoring: string; ec2Base: string };
    errors: { section: AwsHealthSection; message: string }[];
}

/** Rejects with `request()`'s Response on failure, like getAwsHealth. */
export async function getAwsReport(environmentName: string): Promise<AwsEnvironmentReport> {
    const x = await request(`api/system-health/aws/environments/${encodeURIComponent(environmentName)}`, {
        cache: 'no-store',
    }).then((r) => r.json());
    return {
        ...x,
        service: toService(x.service),
        instances: x.instances.map(
            (
                i: Omit<AwsInstance, 'launchedAt' | 'deployedAt'> & { launchedAt: string | null; deployedAt: string | null }
            ) => ({
                ...i,
                launchedAt: toDate(i.launchedAt),
                deployedAt: toDate(i.deployedAt),
            })
        ),
        events: x.events.map((e: Omit<AwsHealthEvent, 'at'> & { at: string }) => ({ ...e, at: new Date(e.at) })),
        metrics: x.metrics && toReportMetrics(x.metrics),
    };
}

export type MetricRange = '30m' | '3h' | '24h';

export interface AwsReportMetrics {
    range: MetricRange;
    /** False for single-instance environments and workers: then only CPU has data. */
    hasLoadBalancer: boolean;
    timestamps: Date[];
    requestsPerMinute: number[];
    errorRate: number[];
    p95Ms: (number | null)[];
    /** Average across the environment's instances. */
    cpuPercent: (number | null)[];
}

const toReportMetrics = (m: Omit<AwsReportMetrics, 'timestamps'> & { timestamps: string[] }): AwsReportMetrics => ({
    ...m,
    timestamps: m.timestamps.map((t) => new Date(t)),
});

export const getAwsReportMetrics = (environmentName: string, range: MetricRange): Promise<AwsReportMetrics> =>
    request(`api/system-health/aws/environments/${encodeURIComponent(environmentName)}/metrics`, {
        query: { range },
        cache: 'no-store',
    })
        .then((r) => r.json())
        .then(toReportMetrics);

export interface AwsInstanceLog {
    instanceId: string;
    at: Date | null;
    /** This instance uploaded, but its file couldn't be read. */
    error: string | null;
    /** Each file's last lines, e.g. /var/log/web.stdout.log. */
    files: { name: string; lines: string[] }[];
}

export interface AwsTailLogs {
    /** Nothing could be fetched, e.g. a missing IAM permission. */
    error: string | null;
    instances: AwsInstanceLog[];
    /** Instances that didn't upload before the deadline. */
    missing: string[];
}

/** Asks EB to collect the last lines of every log on every instance; takes 10-40 seconds. */
export const fetchAwsLogs = (environmentName: string): Promise<AwsTailLogs> =>
    request(`api/system-health/aws/environments/${encodeURIComponent(environmentName)}/logs`, { method: 'POST' })
        .then((r) => r.json())
        .then(
            (x: Omit<AwsTailLogs, 'instances'> & { instances: (Omit<AwsInstanceLog, 'at'> & { at: string | null })[] }) => ({
                ...x,
                instances: x.instances.map((l) => ({ ...l, at: toDate(l.at) })),
            })
        );

/** The backend's own message for a failed call (e.g. AWS throttling), else a generic one. */
export const errorText = async (e: unknown) => (await extractErrorMessage(e)) ?? describeError(e);

export interface SlowRequest {
    /** The endpoint, e.g. "GET /v1/landing-page-image/{imageId}". */
    name: string;
    p50Ms: number | null;
    p95Ms: number | null;
    count: number;
    /** 0 to 1. */
    failureRate: number | null;
    /** Sentry's page for this endpoint, with example slow requests. */
    link: string;
}

// ---- Sentry ----

interface HealthResult<T> {
    data: T | null;
    error: string | null;
    fetchedAt: string;
}

interface SentryIssueDto {
    shortId: string;
    title: string;
    level: string;
    permalink: string;
    count: string;
    userCount: number;
    firstSeen: string | null;
    lastSeen: string | null;
    project: { slug: string } | null;
}

export interface SentryIssuesResult {
    issues: SentryIssue[] | null;
    error: string | null;
}

/** Never rejects: a failed call comes back as `error` so the page can say Sentry isn't connected. */
export async function getSentryIssues(environment?: string): Promise<SentryIssuesResult> {
    try {
        const text = await request('api/system-health/sentry/issues', {
            query: environment ? { environment } : undefined,
            // Live data, polled: never let the browser answer from its cache. Before this endpoint
            // existed the backend's SPA fallback served index.html here with no Cache-Control, and
            // browsers kept replaying that cached HTML.
            cache: 'no-store',
        }).then((x) => x.text());
        let res: HealthResult<SentryIssueDto[]>;
        try {
            res = JSON.parse(text);
        } catch {
            const preview = text.trim().slice(0, 80) || '(empty body)';
            return { issues: null, error: `MacGyver's backend sent a response that isn't JSON. It starts with: ${preview}` };
        }
        return {
            error: res.error,
            issues:
                res.data?.map((i) => ({
                    shortId: i.shortId,
                    title: i.title,
                    project: i.project?.slug ?? '',
                    level: i.level as SentryIssue['level'],
                    count: Number(i.count),
                    users: i.userCount,
                    lastSeen: new Date(i.lastSeen ?? Date.now()),
                    firstSeen: i.firstSeen ? new Date(i.firstSeen) : null,
                    permalink: i.permalink,
                })) ?? null,
        };
    } catch (e) {
        console.error('[SystemHealth] loading Sentry issues failed', e);
        return { issues: null, error: describeError(e) };
    }
}

// ---- GitHub + Linear (Activity and Customers tabs) ----

export interface SourceResult<T> {
    /** Null when the source failed or isn't configured; see `error`. */
    data: T | null;
    error: string | null;
}

/** GETs a HealthResult endpoint. Never rejects: failures come back as `error`, like getSentryIssues. */
async function getHealthResult<T, R>(path: string, map: (data: T) => R): Promise<SourceResult<R>> {
    try {
        const text = await request(path, { cache: 'no-store' }).then((x) => x.text());
        let res: HealthResult<T>;
        try {
            res = JSON.parse(text);
        } catch {
            const preview = text.trim().slice(0, 80) || '(empty body)';
            return { data: null, error: `MacGyver's backend sent a response that isn't JSON. It starts with: ${preview}` };
        }
        return { data: res.data == null ? null : map(res.data), error: res.error };
    } catch (e) {
        console.error(`[SystemHealth] loading ${path} failed`, e);
        return { data: null, error: describeError(e) };
    }
}

type Json<T> = { [K in keyof T]: T[K] extends Date ? string : T[K] };

export interface Repo {
    name: string;
    description: string | null;
    defaultBranch: string | null;
    pushedAt: Date | null;
}

export interface GitHubActivity {
    openPrs: OpenPr[];
    mergedPrs: MergedPr[];
    /** Unarchived repos, most recently pushed first. */
    repos: Repo[];
}

export const getGitHubActivity = () =>
    getHealthResult<
        {
            openPrs: Json<OpenPr>[];
            mergedPrs: Json<MergedPr>[];
            repos: (Omit<Repo, 'pushedAt'> & { pushedAt: string | null })[];
        },
        GitHubActivity
    >('api/system-health/github', (d) => ({
        openPrs: d.openPrs.map((p) => ({ ...p, openedAt: new Date(p.openedAt) })),
        mergedPrs: d.mergedPrs.map((p) => ({ ...p, mergedAt: new Date(p.mergedAt) })),
        repos: (d.repos ?? []).map((r) => ({ ...r, pushedAt: r.pushedAt ? new Date(r.pushedAt) : null })),
    }));

export interface PullRequestDetail {
    repo: string;
    number: number;
    title: string;
    url: string;
    /** Markdown, as written on GitHub. */
    body: string | null;
    author: string;
    state: OpenPr['state'];
    base: string;
    head: string;
    mergeable: 'MERGEABLE' | 'CONFLICTING' | 'UNKNOWN';
    additions: number;
    deletions: number;
    changedFiles: number;
    linearId: string | null;
    openedAt: Date;
    requestedReviewers: string[];
    reviews: { author: string; state: 'APPROVED' | 'CHANGES_REQUESTED' | 'COMMENTED' | 'DISMISSED'; at: string | null }[];
    checks: { name: string; state: 'passing' | 'failing' | 'pending' | 'skipped'; url: string | null }[];
    /** The first 100 files. `patch` is null for binary or very large files. */
    files: { path: string; status: string; additions: number; deletions: number; patch: string | null }[];
}

export const getPullRequest = (repo: string, number: number) =>
    getHealthResult<Json<PullRequestDetail>, PullRequestDetail>(
        `api/system-health/github/pulls/${encodeURIComponent(repo)}/${number}`,
        (d) => ({ ...d, openedAt: new Date(d.openedAt) })
    );

export const getLinearIssues = () =>
    getHealthResult<(Json<LinearIssue> & { completedAt: string | null })[], LinearIssue[]>('api/system-health/linear', (d) =>
        d.map((i) => ({
            ...i,
            // Done issues sort and age by when they were finished, not last touched.
            updatedAt: new Date(i.completedAt ?? i.updatedAt),
        }))
    );

/** The service's slowest endpoints in the last hour, from Sentry's tracing. Never rejects. */
export const getSlowRequests = (environmentName: string) =>
    getHealthResult<{ link: string; requests: SlowRequest[] }, { link: string; requests: SlowRequest[] }>(
        `api/system-health/aws/environments/${encodeURIComponent(environmentName)}/slow-requests`,
        (d) => d
    );

/** Turns whatever `request()` rejected with into a sentence that says what to do about it. */
export function describeError(e: unknown): string {
    if (e instanceof Response) {
        if (e.status === 401) return 'Your MacGyver session has expired. Log in again.';
        if (e.status === 403) return "Your MacGyver user doesn't have access to the system health data.";
        if (e.status >= 500) return `MacGyver's backend failed (${e.status}). Check the backend log for the cause.`;
        return `MacGyver's backend answered ${e.status} ${e.statusText}.`;
    }
    if (e instanceof SyntaxError)
        return "MacGyver's backend sent a response that isn't JSON. It may be restarting; try again in a moment.";
    if (e instanceof TypeError) return "Couldn't reach MacGyver's backend. Check that it's running.";
    return 'Something unexpected went wrong. The browser console has the details.';
}

// ---- shared shapes ----

export interface SentryIssue {
    shortId: string;
    title: string;
    project: string;
    level: 'error' | 'warning' | 'fatal';
    count: number;
    users: number;
    lastSeen: Date;
    firstSeen?: Date | null;
    permalink?: string;
}

export interface MergedPr {
    repo: string;
    number: number;
    title: string;
    author: string;
    base: string;
    additions: number;
    deletions: number;
    linearId?: string;
    mergedAt: Date;
    url?: string;
}

export interface LinearIssue {
    id: string;
    title: string;
    /** Workflow state as named in Linear, e.g. "Ready to test". */
    state: string;
    /** Linear's category: in-flight issues are 'started', done ones 'completed'. */
    stateType: 'started' | 'completed';
    /** Column order on the team's board. */
    statePosition: number;
    assignee: string | null;
    updatedAt: Date;
    url?: string;
}

export type PrState = 'draft' | 'review' | 'changes' | 'approved';
export type ChecksState = 'passing' | 'failing' | 'pending';

export interface OpenPr {
    repo: string;
    number: number;
    title: string;
    author: string;
    state: PrState;
    /** Null when the PR has no checks at all. */
    checks: ChecksState | null;
    url?: string;
    additions: number;
    deletions: number;
    linearId?: string;
    /** Asked to review and not done so yet. */
    reviewers?: string[];
    openedAt: Date;
}
